import { type ToastId, useToast, type UseToastOptions } from '@chakra-ui/react';
import { theme } from '@ui/config/theme';
import { useEffect, useMemo, useRef } from 'react';

// TODO: Use standard options object for methods & existing consumers instead of position-based

interface ToastOptions extends UseToastOptions {
  preventDups?: boolean;
  logMeta?: unknown;
}

export const useValidationToast = () => {
  const toast = useToast();
  const closeAllIdsRef = useRef<(string | number)[]>([]);

  useEffect(() => {
    return () => {
      closeAllToasts();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toastAndSaveRef = (options: ToastOptions, persist = false): ToastId => {
    const { status, title, description, preventDups, id: inputId } = options;

    if (status === 'error') {
      console.error(`[ERROR] ${title}: ${description}`, options.logMeta);
    }

    if (status === 'warning') {
      console.warn(`[WARNING] ${title}: ${description}`, options.logMeta);
    }

    if (preventDups && inputId && toast.isActive(inputId)) {
      return inputId;
    }

    const toastId = toast(options);
    if (!persist) {
      closeAllIdsRef.current.push(toastId);
    }
    return toastId;
  };

  /**
   *
   * @param title
   * @param description
   * @param sticky
   * @param persist
   *
   * @returns toastId
   */
  const genericErrorToast = (
    title: string,
    description?: string,
    sticky = false,
    persist = false,
    options: ToastOptions = {},
  ): ToastId => {
    return toastAndSaveRef(
      {
        title,
        description,
        status: 'error',
        isClosable: sticky,
        duration: sticky ? null : 4000,
        position: 'top-right',
        ...options,
      },
      persist,
    );
  };
  const genericWarningToast = (
    title: string,
    description?: string,
    sticky = false,
    persist = false,
    options: ToastOptions = {},
  ): ToastId => {
    return toastAndSaveRef(
      {
        title,
        description,
        status: 'warning',
        isClosable: sticky,
        duration: sticky ? null : 4000,
        position: 'top-right',
        containerStyle: {
          color: theme.colors.validation.warning,
        },
        ...options,
      },
      persist,
    );
  };
  const genericSuccessToast = (
    title: string,
    description?: string,
    sticky = false,
    persist = false,
    options: ToastOptions = {},
  ): ToastId => {
    return toastAndSaveRef(
      {
        title,
        description,
        status: 'success',
        isClosable: sticky,
        duration: sticky ? null : 2000,
        position: 'top-right',
        ...options,
      },
      persist,
    );
  };
  const closeAllToasts = () => {
    for (const id of closeAllIdsRef.current) {
      toast.close(id);
    }
  };
  /**
   * @param id only nullable so consumers don't have to check; does nothing if null
   */
  const closeToast = (id: ToastId | null) => {
    if (id) {
      toast.close(id);
    }
  };

  return useMemo(
    () => ({
      genericErrorToast,
      genericWarningToast,
      genericSuccessToast,
      clearAllToasts: closeAllToasts,
      clearToast: closeToast,
      isActive: toast.isActive,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast],
  );
};
